import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { apiError, formatINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner, EmptyState, Card, Modal, Input, Select, PrimaryButton, GhostButton } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { MapPin, Dumbbell, Plus, ArrowLeft, Clock, Trash2, CalendarCheck, CreditCard, Search } from "lucide-react";
import { toast } from "sonner";

function loadRzp() {
  return new Promise((res) => {
    if (window.Razorpay) return res(true);
    const s = document.createElement("script"); s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => res(true); s.onerror = () => res(false); document.body.appendChild(s);
  });
}

export default function Facilities() {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [sport, setSport] = useState("");
  const [city, setCity] = useState("");
  useEffect(() => { api.get("/facilities").then((r) => setList(r.data)).catch(() => setList([])); }, []);
  if (!list) return <Spinner />;
  const filtered = list.filter((f) => (!sport || f.sport === sport) && (!city || (f.city || "").toLowerCase().includes(city.toLowerCase())));

  return (
    <div className="animate-fadeUp">
      <button onClick={() => navigate("/app/play")} className="mb-4 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back to Play</button>
      <PageHeader title="Find a Sports Ground" subtitle="Discover and book verified sports facilities" />
      <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:w-2/3">
        <Input testId="fac-filter-sport" placeholder="Filter by sport" value={sport} onChange={(e) => setSport(e.target.value)} />
        <Input testId="fac-filter-city" placeholder="Filter by city" value={city} onChange={(e) => setCity(e.target.value)} />
      </div>
      {filtered.length === 0 ? <EmptyState icon={Dumbbell} title="No facilities yet" text="Verified facilities with bookable units appear here." /> : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((f) => (
            <button key={f.id} data-testid={`facility-card-${f.id}`} onClick={() => navigate(`/app/facilities/${f.id}`)}
              className="rounded-2xl border border-border bg-card p-5 text-left transition-all hover:-translate-y-1 hover:border-primary/50">
              <div className="flex items-start justify-between"><h3 className="font-display text-lg font-semibold">{f.name}</h3><StatusBadge status="verified" /></div>
              {f.sport && <p className="mt-1 text-sm capitalize text-primary">{f.sport}</p>}
              <p className="mt-3 flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3 w-3" /> {f.city}, {f.state}</p>
              <p className="mt-2 text-xs text-muted-foreground">{f.unit_count} bookable unit(s)</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function FacilityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const [data, setData] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [unitForm, setUnitForm] = useState({ name: "", sport: "", price_per_slot: 500, slot_minutes: 60, open_time: "06:00", close_time: "22:00", capacity: 10 });
  const [booking, setBooking] = useState(null); // unit being booked
  const [bk, setBk] = useState({ date: "", start: "", end: "" });

  const load = () => api.get(`/facilities/${id}`).then((r) => setData(r.data)).catch(() => navigate("/app/facilities"));
  useEffect(() => { load(); }, [id]);
  if (!data) return <Spinner />;
  const { facility, units } = data;
  const isOwner = hasRole("ops", "admin") || facility.created_by === user?.id;

  const addUnit = async (e) => {
    e.preventDefault();
    try { await api.post(`/facilities/${id}/units`, { ...unitForm, price_per_slot: Number(unitForm.price_per_slot), slot_minutes: Number(unitForm.slot_minutes), capacity: Number(unitForm.capacity) }); toast.success("Unit added"); setAddOpen(false); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const delUnit = async (uid) => { try { await api.delete(`/facilities/units/${uid}`); load(); } catch (e) { toast.error(apiError(e)); } };

  const doBook = async (e) => {
    e.preventDefault();
    try {
      const { data: fb } = await api.post(`/facilities/units/${booking.id}/book`, bk);
      toast.success("Slot held - completing payment…");
      // pay
      const { data: order } = await api.post(`/facility-bookings/${fb.id}/payment/order`);
      const verify = (pid, sig) => api.post(`/facility-bookings/${fb.id}/payment/verify`, { razorpay_order_id: order.order_id, razorpay_payment_id: pid, razorpay_signature: sig });
      if (order.simulated || !order.key_id) {
        await verify("pay_sim", "sim"); toast.success("Booking confirmed!"); setBooking(null); navigate("/app/profile"); return;
      }
      const ok = await loadRzp(); if (!ok) { toast.error("Gateway failed"); return; }
      new window.Razorpay({ key: order.key_id, amount: order.amount, currency: "INR", name: "SPORA1", description: order.booking_reference, order_id: order.order_id,
        prefill: { name: user?.name, email: user?.email }, theme: { color: "#FF6B00" },
        handler: async (res) => { try { await verify(res.razorpay_payment_id, res.razorpay_signature); toast.success("Booking confirmed!"); setBooking(null); navigate("/app/profile"); } catch (er) { toast.error(apiError(er)); } } }).open();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="animate-fadeUp">
      <button onClick={() => navigate("/app/facilities")} className="mb-4 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> All facilities</button>
      <PageHeader title={facility.name} subtitle={`${facility.city}, ${facility.state}`} action={isOwner && <div className="flex gap-2"><GhostButton data-testid="facility-calendar-btn" onClick={() => navigate(`/app/owner-calendar?facility=${id}`)}><CalendarCheck className="h-4 w-4" /> Calendar</GhostButton><PrimaryButton data-testid="add-unit-btn" onClick={() => setAddOpen(true)}><Plus className="h-4 w-4" /> Add unit</PrimaryButton></div>} />
      {facility.description && <p className="mb-5 max-w-2xl text-sm text-muted-foreground">{facility.description}</p>}

      {units.length === 0 ? <EmptyState icon={Dumbbell} title="No bookable units yet" text={isOwner ? "Add a ground/court with pricing and hours." : "This facility hasn't published bookable units."} /> : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {units.map((u) => (
            <Card key={u.id} data-testid={`unit-card-${u.id}`}>
              <div className="flex items-start justify-between">
                <div><h3 className="font-display text-lg font-semibold">{u.name}</h3><p className="text-sm capitalize text-primary">{u.sport}</p></div>
                {isOwner && <button onClick={() => delUnit(u.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>}
              </div>
              <p className="mt-3 flex items-center gap-1 text-xs text-muted-foreground"><Clock className="h-3 w-3" /> {u.open_time}–{u.close_time} · {u.slot_minutes}min slots</p>
              <p className="mt-1 font-display text-2xl font-bold">{formatINR(u.price_per_slot)}<span className="text-sm font-normal text-muted-foreground">/slot</span></p>
              <PrimaryButton data-testid={`book-unit-${u.id}`} className="mt-3 w-full" onClick={() => { setBooking(u); setBk({ date: "", start: "", end: "" }); }}><CalendarCheck className="h-4 w-4" /> Book</PrimaryButton>
            </Card>
          ))}
        </div>
      )}

      <Modal open={!!booking} onClose={() => setBooking(null)} title={`Book ${booking?.name || ""}`}>
        <form onSubmit={doBook} className="space-y-4">
          <Input label="Date" testId="book-date" type="date" required value={bk.date} onChange={(e) => setBk({ ...bk, date: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Start" testId="book-start" type="time" required value={bk.start} onChange={(e) => setBk({ ...bk, start: e.target.value })} />
            <Input label="End" testId="book-end" type="time" required value={bk.end} onChange={(e) => setBk({ ...bk, end: e.target.value })} />
          </div>
          <p className="text-xs text-muted-foreground">Confirmed slots can't overlap. You'll pay {formatINR(booking?.price_per_slot)} per {booking?.slot_minutes}min.</p>
          <div className="flex justify-end gap-2"><GhostButton type="button" onClick={() => setBooking(null)}>Cancel</GhostButton><PrimaryButton data-testid="confirm-book-btn" type="submit"><CreditCard className="h-4 w-4" /> Book & pay</PrimaryButton></div>
        </form>
      </Modal>

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Add bookable unit" wide>
        <form onSubmit={addUnit} className="grid gap-4 sm:grid-cols-2">
          <Input label="Unit name" testId="unit-name" required value={unitForm.name} onChange={(e) => setUnitForm({ ...unitForm, name: e.target.value })} className="sm:col-span-2" placeholder="e.g. 5-a-side Turf A" />
          <Input label="Sport" testId="unit-sport" value={unitForm.sport} onChange={(e) => setUnitForm({ ...unitForm, sport: e.target.value })} />
          <Input label="Price per slot (₹)" testId="unit-price" type="number" value={unitForm.price_per_slot} onChange={(e) => setUnitForm({ ...unitForm, price_per_slot: e.target.value })} />
          <Input label="Slot minutes" testId="unit-slot" type="number" value={unitForm.slot_minutes} onChange={(e) => setUnitForm({ ...unitForm, slot_minutes: e.target.value })} />
          <Input label="Capacity" testId="unit-capacity" type="number" value={unitForm.capacity} onChange={(e) => setUnitForm({ ...unitForm, capacity: e.target.value })} />
          <Input label="Opens" testId="unit-open" type="time" value={unitForm.open_time} onChange={(e) => setUnitForm({ ...unitForm, open_time: e.target.value })} />
          <Input label="Closes" testId="unit-close" type="time" value={unitForm.close_time} onChange={(e) => setUnitForm({ ...unitForm, close_time: e.target.value })} />
          <div className="flex justify-end gap-2 sm:col-span-2"><GhostButton type="button" onClick={() => setAddOpen(false)}>Cancel</GhostButton><PrimaryButton data-testid="save-unit-btn" type="submit">Add unit</PrimaryButton></div>
        </form>
      </Modal>
    </div>
  );
}
